/**
 * Load Group Tool
 * 
 * Simulates loading an external file as a Model Group.
 */

import { v4 as uuidv4 } from 'uuid';
import { GroupElement, GroupGeometryData } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import i18n from '../i18n';

export class LoadGroupTool extends BaseTool {
    id = 'load_group';
    name = 'Load as Group';
    cursor = 'copy';

    private pickedPath: string | null = null;

    onActivate(): void {
        // Dialog-driven command: BIM opens a file dialog for Load as Group.
        void this.pickFile('.rvt,.yap,.json').then((file) => {
            if (file) {
                this.pickedPath = file.name;
                this.notify(i18n.t('tools.notify.groupFileSelected', { fileName: file.name }));
            } else {
                this.exitToSelect();
            }
        });
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        // Insert the picked file as a group at the clicked location.
        const point = this.getIntersection(event);
        if (point) {
            this.createGroup(this.snapToGrid(point));
            this.exitToSelect(); // Single placement, then back to Select
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.updatePreview(this.snapToGrid(point));
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private updatePreview(point: THREE.Vector3) {
        if (!this.context) return;

        this.context.appStore.setToolPreview({
            type: 'box',
            position: point,
            size: [3, 3, 2.7], // Typical room size preview
            color: '#aaaaaa',
            opacity: 0.5
        });
    }

    private createGroup(position: THREE.Vector3) {
        if (!this.context) return;

        const sourceName = this.pickedPath ?? 'TypicalRoom.rvt';

        // 1. Create Group Element from the picked source
        const geometryData: GroupGeometryData = {
            origin: [position.x, position.y, position.z],
            memberIds: []
        };

        const group: GroupElement = {
            id: uuidv4(),
            category: 'Model Groups',
            type: `Group: ${sourceName}`,
            parameters: {
                'Origin Level': 'Level 1',
                'Reference Level': 'Level 1'
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(group);
        this.notify(i18n.t('tools.notify.groupLoadedPlaced', { name: sourceName }), 'success');
    }
}
